import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PersonService } from '../../../core/api/person.service';
import { UserRole } from '../../../core/auth/user-role';
import { PersonDto } from '../../../core/dto/person';
import { NotificationService } from '../../../core/services/notification.service';
import { ChangesService } from '../../../core/changes-warning/changes.service';

const ROLE_LABELS: { [key: string]: string } = {
    ROLE_USER: 'User',
    ROLE_EDUCATION_EDITOR: 'Education Editor',
    ROLE_ADMIN_LIGHT: 'Admin Light',
    ROLE_ADMIN: 'Admin',
    ROLE_ORGANIZATION: 'Organisation',
    ROLE_SAR: 'SAR',
    ROLE_EXTERNAL: 'External',
};

const ROLES = Object.values(UserRole)
    .map((role) => ({ value: role, text: ROLE_LABELS[role] }))
    .sort((a, b) => a.text.localeCompare(b.text));

@Component({
    selector: 'isav-people-edit-admin-form',
    templateUrl: './people-edit-admin-form.html',
    styleUrls: ['./people-edit-admin-form.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleEditAdminForm implements OnChanges, OnInit, OnDestroy {
    @Input() value: PersonDto;

    loading = false;
    rolesControl = new UntypedFormControl([]);
    passwordControl = new UntypedFormControl('');
    active: boolean;

    readonly roles = ROLES;

    constructor(
        private personService: PersonService,
        private notificationService: NotificationService,
        private _cdRef: ChangeDetectorRef,
        private changesService: ChangesService
    ) {}

    ngOnInit() {
        this.changesService.connect(this.rolesControl);
        this.changesService.connect(this.passwordControl);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.value && this.value) {
            this.rolesControl.reset(this.value.roles);
            this.active = this.value.active;
        }
    }

    ngOnDestroy() {
        this.changesService.disconnect(this.rolesControl);
        this.changesService.disconnect(this.passwordControl);
    }

    activate() {
        this.loading = true;
        this.personService.activate(this.value['@id']).subscribe(
            () => {
                this.loading = false;
                this.active = true;
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully activated user',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }

    deactivate() {
        this.loading = true;
        this.personService.deactivate(this.value['@id']).subscribe(
            () => {
                this.loading = false;
                this.active = false;
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully deactivated user',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }

    changeRoles() {
        this.loading = true;
        const value = this.rolesControl.value;
        this.personService.changeRoles(this.value['@id'], value).subscribe(
            () => {
                this.loading = false;
                this.rolesControl.reset(value);
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully changed roles',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }

    changePassword() {
        this.loading = true;
        this.personService.changePassword(this.value['@id'], this.passwordControl.value).subscribe(
            () => {
                this.loading = false;
                this.passwordControl.reset();
                this.notificationService.add({
                    title: 'Success',
                    message: 'Successfully changed password',
                    type: 'success',
                    delay: 5000,
                });
                this._cdRef.markForCheck();
            },
            () => {
                this.loading = false;
                this._cdRef.markForCheck();
            }
        );
    }
}
